/**
 * Search Enhancements for Nexvo Theme
 *
 * Enhances the existing PredictiveSearch custom element with:
 *   - Synonym expansion (configurable map)
 *   - Typo tolerance via Levenshtein distance
 *   - "Did you mean?" suggestions in both dropdown and results page
 *   - GA4 analytics (search, clicks, conversions)
 *
 * Does NOT replace any UI — hooks into existing Nexvo search flow.
 */
(function () {
  'use strict';

  // =============================================================
  // SYNONYM MAP
  // Keys and values are lowercased. Add domain-specific terms here.
  // =============================================================
  var SYNONYM_MAP = {
    'fridge':       ['refrigerator', 'cooler'],
    'refrigerator': ['fridge', 'cooler'],
    'oven':         ['range', 'convection oven', 'combi oven'],
    'range':        ['oven', 'stove', 'cooking range'],
    'stove':        ['range', 'burner', 'cooktop'],
    'freezer':      ['chest freezer', 'reach-in freezer', 'blast chiller'],
    'mixer':        ['stand mixer', 'planetary mixer', 'dough mixer'],
    'fryer':        ['deep fryer', 'countertop fryer', 'floor fryer'],
    'grill':        ['griddle', 'charbroiler', 'broiler'],
    'griddle':      ['grill', 'flat top', 'plancha'],
    'dishwasher':   ['dish machine', 'warewasher', 'glasswasher'],
    'prep table':   ['work table', 'prep station', 'worktop'],
    'shelving':     ['shelf', 'rack', 'storage rack', 'wire shelving'],
    'ice machine':  ['ice maker', 'ice bin', 'ice dispenser'],
    'steam table':  ['hot food table', 'buffet table', 'serving station'],
    'slicer':       ['meat slicer', 'deli slicer', 'food slicer'],
    'blender':      ['food blender', 'bar blender', 'immersion blender'],
    'warmer':       ['food warmer', 'heat lamp', 'holding cabinet'],
    'sink':         ['compartment sink', 'hand sink', 'utility sink'],
    'pan':          ['sheet pan', 'hotel pan', 'steam pan', 'baking pan'],
    'cooktop':      ['stove', 'range', 'burner'],
    'broiler':      ['salamander', 'charbroiler', 'grill'],
    'hood':         ['exhaust hood', 'ventilation hood', 'range hood'],
    'table':        ['work table', 'prep table', 'equipment stand'],
    'dispenser':    ['cup dispenser', 'napkin dispenser', 'condiment dispenser']
  };

  // Build a flat lookup of all known words (keys + first word of each value)
  // for fast Levenshtein comparison
  var KNOWN_WORDS = Object.keys(SYNONYM_MAP);

  // =============================================================
  // LEVENSHTEIN DISTANCE
  // =============================================================
  function levenshtein(a, b) {
    if (a === b) return 0;
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    var prev = [];
    var curr = [];
    var i, j;

    for (j = 0; j <= b.length; j++) prev[j] = j;

    for (i = 1; i <= a.length; i++) {
      curr[0] = i;
      for (j = 1; j <= b.length; j++) {
        if (a[i - 1] === b[j - 1]) {
          curr[j] = prev[j - 1];
        } else {
          curr[j] = 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
        }
      }
      var tmp = prev;
      prev = curr;
      curr = tmp;
    }
    return prev[b.length];
  }

  /**
   * Find the closest known synonym key for a potentially misspelled term.
   * Returns { match, distance } or null if nothing is close enough.
   */
  function findFuzzyMatch(term) {
    var lowerTerm = term.toLowerCase();
    var bestMatch = null;
    var bestDist = Infinity;

    for (var i = 0; i < KNOWN_WORDS.length; i++) {
      var key = KNOWN_WORDS[i];
      // Skip multi-word keys for single-word input
      if (key.indexOf(' ') !== -1 && lowerTerm.indexOf(' ') === -1) continue;

      var dist = levenshtein(lowerTerm, key);
      // Threshold: max 1 edit for 3-4 char words, max 2 for 5+ chars
      var maxDist = key.length <= 4 ? 1 : 2;

      if (dist > 0 && dist <= maxDist && dist < bestDist) {
        bestDist = dist;
        bestMatch = key;
      }
    }
    return bestMatch ? { match: bestMatch, distance: bestDist } : null;
  }

  // =============================================================
  // QUERY ANALYSIS
  // Returns: { synonym, correction, original }
  // - synonym: the best synonym to add to the query (or null)
  // - correction: a "did you mean X?" term if typo detected (or null)
  // =============================================================
  function analyzeQuery(query) {
    var terms = query.toLowerCase().trim().split(/\s+/);
    var result = { synonym: null, correction: null, original: query };

    // Try multi-word match first (e.g. "ice machine", "prep table")
    var fullPhrase = terms.join(' ');
    if (SYNONYM_MAP[fullPhrase]) {
      result.synonym = SYNONYM_MAP[fullPhrase][0];
      return result;
    }

    // Try each individual term
    for (var i = 0; i < terms.length; i++) {
      var term = terms[i];
      if (term.length < 3) continue; // skip short words like "a", "of"

      // Direct synonym hit
      if (SYNONYM_MAP[term]) {
        result.synonym = SYNONYM_MAP[term][0];
        return result;
      }

      // Fuzzy match — possible typo
      var fuzzy = findFuzzyMatch(term);
      if (fuzzy) {
        result.correction = fuzzy.match;
        // Also provide the first synonym for the corrected term
        if (SYNONYM_MAP[fuzzy.match]) {
          result.synonym = SYNONYM_MAP[fuzzy.match][0];
        }
        return result;
      }
    }

    return result;
  }

  // =============================================================
  // GA4 ANALYTICS
  // =============================================================
  var _analyticsTimer = null;
  var _lastTrackedQuery = '';

  function trackEvent(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });

    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    }
  }

  /**
   * Debounced search tracking — only fires after user stops typing for 1.2s.
   * Prevents flooding GA4 with partial queries.
   */
  function trackSearchDebounced(query) {
    clearTimeout(_analyticsTimer);
    _analyticsTimer = setTimeout(function () {
      if (query && query.length >= 2 && query !== _lastTrackedQuery) {
        _lastTrackedQuery = query;
        trackEvent('search', { search_term: query });
      }
    }, 1200);
  }

  // =============================================================
  // PATCH: PredictiveSearch element
  // Hooks into the existing class without replacing it.
  // =============================================================
  function patchPredictiveSearch() {
    var elements = document.querySelectorAll('predictive-search');

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el._searchEnhanced) continue;
      el._searchEnhanced = true;
      enhanceElement(el);
    }
  }

  function enhanceElement(el) {
    var _lastAnalysis = { synonym: null, correction: null, original: '' };

    // --- Patch setupURL to inject synonym into query ---
    var _origSetupURL = el.setupURL.bind(el);

    el.setupURL = function () {
      var query = this.getQuery();
      _lastAnalysis = analyzeQuery(query);

      // If we found a synonym, append it with OR to broaden results
      if (_lastAnalysis.synonym && !_lastAnalysis.correction) {
        var url = new URL(window.shopUrl + FoxTheme.routes.predictive_search_url);
        var searchTerm = query;

        // Respect product type filter
        if (this.searchProductTypes && this.searchProductTypes.value !== '') {
          searchTerm = 'product_type:' + this.searchProductTypes.value + ' AND ' + query;
        }

        // Append synonym with OR — Shopify Predictive Search supports this
        searchTerm += ' OR ' + _lastAnalysis.synonym;

        url.searchParams.set('q', searchTerm);
        url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
        return url;
      }

      // If typo was detected, search with the corrected term instead
      if (_lastAnalysis.correction) {
        var url = new URL(window.shopUrl + FoxTheme.routes.predictive_search_url);
        var correctedSearch = query.toLowerCase().replace(
          _lastAnalysis.original.toLowerCase().split(/\s+/).find(function(t) { return findFuzzyMatch(t); }) || '',
          _lastAnalysis.correction
        );

        if (this.searchProductTypes && this.searchProductTypes.value !== '') {
          correctedSearch = 'product_type:' + this.searchProductTypes.value + ' AND ' + correctedSearch;
        }

        url.searchParams.set('q', correctedSearch);
        url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
        return url;
      }

      return _origSetupURL();
    };

    // --- Patch renderSearchResults to inject "Did you mean?" ---
    var _origRender = el.renderSearchResults.bind(el);

    el.renderSearchResults = function (responseText) {
      _origRender(responseText);

      // Remove any previous "did you mean?" banner
      var existing = el.querySelector('.search-did-you-mean');
      if (existing) existing.remove();

      // Show "did you mean?" only if there was a typo correction
      if (_lastAnalysis.correction) {
        var resultsContainer = el.querySelector('[id^="PredictiveSearchResults-"]');
        if (resultsContainer) {
          insertDidYouMean(resultsContainer, _lastAnalysis.correction, el);
        }
      }

      // Debounced analytics
      var query = el.getQuery();
      if (query) trackSearchDebounced(query);
    };

    // --- Click tracking on predictive search results ---
    el.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var isResult = link.closest('.predictive-search-result__list-item') ||
                     link.closest('.card-product') ||
                     link.closest('.search__most-searched-keywords');
      if (!isResult) return;

      trackEvent('select_content', {
        content_type: 'predictive_search_result',
        search_term: el.getQuery(),
        item_url: link.getAttribute('href'),
        item_title: (link.textContent || '').trim().substring(0, 80)
      });
    });
  }

  // =============================================================
  // "DID YOU MEAN?" UI
  // Inserted at the top of the predictive search results area.
  // =============================================================
  function insertDidYouMean(container, correctedTerm, searchEl) {
    var div = document.createElement('div');
    div.className = 'search-did-you-mean';
    div.setAttribute('style',
      'padding:10px 0 6px;font-size:14px;color:var(--color-foreground-secondary,#666);'
    );
    div.innerHTML =
      'Did you mean: <a href="#" class="reversed-link" style="font-weight:600;text-decoration:underline;">' +
      escapeHTML(correctedTerm) +
      '</a>?';

    container.prepend(div);

    div.querySelector('a').addEventListener('click', function (e) {
      e.preventDefault();
      var input = searchEl.querySelector('input[type="search"]');
      if (input) {
        input.value = correctedTerm;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      trackEvent('search_correction', {
        original_term: searchEl.getQuery(),
        corrected_term: correctedTerm
      });
    });
  }

  function escapeHTML(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // =============================================================
  // SEARCH RESULTS PAGE (/search) enhancements
  // =============================================================
  function enhanceSearchResultsPage() {
    if (window.location.pathname !== '/search') return;

    var urlParams = new URLSearchParams(window.location.search);
    var query = urlParams.get('q');
    if (!query) return;

    // Persist for cross-page conversion tracking
    try { sessionStorage.setItem('_search_term', query); } catch (e) {}

    // Track search results page view with result count
    var countEl = document.getElementById('ProductCount');
    var resultCount = 0;
    if (countEl) {
      var match = countEl.textContent.match(/(\d+)/);
      if (match) resultCount = parseInt(match[1], 10);
    }

    trackEvent('view_search_results', {
      search_term: query,
      result_count: resultCount
    });

    // "Did you mean?" on zero-result pages
    var emptyState = document.querySelector('.template-search--empty');
    if (emptyState) {
      var analysis = analyzeQuery(query);
      var suggestion = analysis.correction || analysis.synonym;

      if (suggestion) {
        var header = emptyState.querySelector('.template-search__header');
        if (header) {
          var banner = document.createElement('div');
          banner.className = 'search-did-you-mean text-center';
          banner.setAttribute('style', 'margin-top:12px;font-size:16px;');
          banner.innerHTML =
            'Did you mean: <a href="/search?q=' + encodeURIComponent(suggestion) +
            '&type=product&options%5Bprefix%5D=last" style="font-weight:600;text-decoration:underline;">' +
            escapeHTML(suggestion) + '</a>?';
          header.appendChild(banner);
        }
      }
    }

    // Track clicks on search result products
    var productGrid = document.getElementById('ProductsList');
    if (productGrid) {
      productGrid.addEventListener('click', function (e) {
        var link = e.target.closest('a[href]');
        if (!link) return;
        var card = link.closest('.card-product') || link.closest('.f-column');
        if (!card) return;

        trackEvent('select_content', {
          content_type: 'search_result',
          search_term: query,
          item_url: link.getAttribute('href')
        });
      });
    }
  }

  // =============================================================
  // CONVERSION TRACKING
  // Hooks into Nexvo's PubSub system to track add-to-cart after search.
  // =============================================================
  function initConversionTracking() {
    // Wait for FoxTheme.pubsub to be available
    if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) {
      // Fallback: listen for the DOM event
      document.addEventListener('cart:updated', onCartUpdate);
      return;
    }

    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      function (data) {
        var searchTerm = '';
        try { searchTerm = sessionStorage.getItem('_search_term') || ''; } catch (e) {}
        if (!searchTerm) return;

        trackEvent('search_add_to_cart', {
          search_term: searchTerm,
          cart_token: data && data.cart ? data.cart.token : ''
        });
      }
    );
  }

  function onCartUpdate() {
    var searchTerm = '';
    try { searchTerm = sessionStorage.getItem('_search_term') || ''; } catch (e) {}
    if (!searchTerm) return;

    trackEvent('search_add_to_cart', { search_term: searchTerm });
  }

  // =============================================================
  // INIT
  // =============================================================
  function init() {
    patchPredictiveSearch();
    enhanceSearchResultsPage();
    initConversionTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay to ensure PredictiveSearch custom elements are registered
    setTimeout(init, 50);
  }
})();
